import { originalCatalog } from "@marshmallow-duel/game-data";

import type { ArenaMap, PlayerCount, PlayerSlot, WorldObjectDefinition, WorldObjectKind } from "./types";

const objectKinds: Readonly<Record<string, WorldObjectKind>> = Object.freeze({
  obj_wall: "wall",
  obj_wall_left: "wallLeft",
  obj_wall_right: "wallRight",
  obj_wall_bottom: "wallBottom",
  obj_solidwall: "solidWall",
  obj_kicker: "kicker",
  obj_hkickerleft: "horizontalKickerLeft",
  obj_hkickerright: "horizontalKickerRight",
  obj_field: "field",
  obj_floatfield: "floatField",
  obj_teleport: "teleport",
  obj_solidrope: "solidRope",
  obj_solidropetop: "solidRopeTop",
  obj_kickemitter: "kickEmitter",
  obj_kickemitter_bottom: "kickEmitterBottom",
  obj_kickemitter_left: "kickEmitterLeft",
  obj_kickemitter_right: "kickEmitterRight"
});

const titles: Readonly<Record<string, string>> = Object.freeze({
  rm_arena_2_0: "Original",
  rm_arena_2_1: "First best",
  rm_arena_2_2: "Map without a name",
  rm_arena_2_3: "Pyramid of blocks",
  rm_arena_2_4: "Ex",
  rm_arena_2_5: "Air Islands",
  rm_arena_2_6: "Kickers",
  rm_arena_2_7: "Spiderland",
  rm_arena_2_8: "Teleport Boxes",
  rm_arena_2_9: "Climbing",
  rm_arena_2_10: "Poseidon's column",
  rm_arena_2_11: "Turbine",
  rm_arena_3_0: "Ex",
  rm_arena_3_1: "Turbine",
  rm_arena_4_0: "Ex",
  rm_arena_4_1: "Turbine"
});

const parseNumber = (code: string, name: string): number | undefined => {
  const match = code.match(new RegExp(`${name}\\s*=\\s*(-?\\d+(?:\\.\\d+)?)`));
  return match ? Number(match[1]) : undefined;
};

const parsePlayerSlot = (code: string): PlayerSlot | null => {
  const slot = parseNumber(code, "pnumber");
  return slot === 1 || slot === 2 || slot === 3 || slot === 4 ? slot : null;
};

export const builtInRoomNames = (playerCount: PlayerCount): readonly string[] => Object.keys(originalCatalog.rooms)
  .filter((name) => name.startsWith(`rm_arena_${playerCount}_`))
  .sort((left, right) => Number(left.split("_").at(-1)) - Number(right.split("_").at(-1)));

export const createBuiltInMap = (roomName: string): ArenaMap => {
  const room = originalCatalog.rooms[roomName];
  if (!room) throw new Error(`Unknown original room: ${roomName}`);
  const countMatch = roomName.match(/^rm_arena_([234])_/);
  if (!countMatch) throw new Error(`Room is not a built-in arena: ${roomName}`);
  const playerCount = Number(countMatch[1]) as PlayerCount;
  const spawnEntries = room.instances
    .filter(({ object }) => object === "obj_player")
    .map((instance) => ({ slot: parsePlayerSlot(instance.creationCode), x: instance.x, y: instance.y }))
    .filter((spawn): spawn is { slot: PlayerSlot; x: number; y: number } => spawn.slot !== null);
  const spawnMap = Object.fromEntries(spawnEntries.map(({ slot, x, y }) => [slot, { x, y }])) as Record<PlayerSlot, { x: number; y: number }>;
  for (let slot = 1; slot <= playerCount; slot += 1) {
    if (!spawnMap[slot as PlayerSlot]) throw new Error(`${roomName} is missing player ${slot}`);
  }

  const objects: WorldObjectDefinition[] = [];
  room.instances.forEach((instance, index) => {
    const kind = objectKinds[instance.object];
    if (!kind) return;
    const object = originalCatalog.objects[instance.object];
    objects.push({
      id: `${roomName}:${index}`,
      kind,
      sourceObject: instance.object,
      sprite: object?.sprite ?? null,
      x: instance.x,
      y: instance.y,
      ...(kind === "teleport" ? {
        targetX: parseNumber(instance.creationCode, "telex") ?? 0,
        targetY: parseNumber(instance.creationCode, "teley") ?? 0
      } : {}),
      ...(kind.startsWith("kickEmitter") ? {
        emitterDelayTicks: parseNumber(instance.creationCode, "delay") ?? 0,
        emitterPeriodTicks: parseNumber(instance.creationCode, "time") ?? 90
      } : {})
    });
  });

  return {
    id: roomName,
    name: titles[roomName] ?? roomName,
    sourceRoom: roomName,
    width: room.width,
    height: room.height,
    playerCount,
    spawns: spawnMap,
    objects,
    fixedAbilityIds: null
  };
};

export const builtInMaps = (playerCount: PlayerCount): readonly ArenaMap[] => builtInRoomNames(playerCount).map(createBuiltInMap);
